import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { XCircle } from 'lucide-react';
import { TransactionVerifier } from '../services/transactionVerifier';
import { PaykuValidator } from '../services/paykuValidator';

const PaykuCancel: React.FC = () => {
  const location = useLocation();
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(5);
  const [updateStatus, setUpdateStatus] = useState<'loading' | 'success' | 'error'>('loading');

  useEffect(() => {
    const processCancellation = async () => {
      try {
        // Obtener el ID de transacción de la URL
        const params = new URLSearchParams(window.location.search);
        const id = params.get('id');
        
        console.log('[CANCEL] ID de transacción cancelada:', id);
        setTransactionId(id);
        
        // Lista para almacenar los IDs de transacción a marcar como cancelados
        const idsToCancel = [];
        
        // 1. Añadir el ID de la URL si existe
        if (id) {
          idsToCancel.push(id);
        }
        
        // 2. Verificar en localStorage/sessionStorage si hay transacciones pendientes
        try {
          const pendingTxStr = sessionStorage.getItem('pendingTransaction') || localStorage.getItem('pendingTransaction');
          if (pendingTxStr) {
            const pendingTx = JSON.parse(pendingTxStr);
            if (pendingTx && pendingTx.id && !idsToCancel.includes(pendingTx.id)) {
              console.log('[CANCEL] ID adicional encontrado en pendingTransaction:', pendingTx.id);
              idsToCancel.push(pendingTx.id);
            }
          }
          
          const paykuTxStr = sessionStorage.getItem('payku_transaction') || localStorage.getItem('payku_transaction');
          if (paykuTxStr) {
            const paykuTx = JSON.parse(paykuTxStr);
            if (paykuTx && paykuTx.transaction_id && !idsToCancel.includes(paykuTx.transaction_id)) {
              console.log('[CANCEL] ID adicional encontrado en payku_transaction:', paykuTx.transaction_id);
              idsToCancel.push(paykuTx.transaction_id);
            }
          }
          
          const lastPaykuTx = localStorage.getItem('last_payku_transaction');
          if (lastPaykuTx && !idsToCancel.includes(lastPaykuTx)) {
            console.log('[CANCEL] ID adicional encontrado en last_payku_transaction:', lastPaykuTx);
            idsToCancel.push(lastPaykuTx);
          }
        } catch (err) {
          console.error('[CANCEL] Error al recuperar IDs adicionales:', err);
        }
        
        console.log('[CANCEL] IDs de transacción a cancelar:', idsToCancel);
        
        // Si no hay IDs para cancelar, mostrar error
        if (idsToCancel.length === 0) {
          setUpdateStatus('error');
          console.error('[CANCEL] No se encontraron IDs de transacción para cancelar');
          return;
        }
        
        // Cancelar todas las transacciones encontradas
        let allSuccessful = true;
        
        for (const txId of idsToCancel) {
          // VERIFICACIÓN DOBLE: Primero verificar con Payku el estado real
          try {
            const paykuStatus = await PaykuValidator.verifyPaykuPaymentStatus(txId);
            console.log(`[CANCEL] Estado de transacción ${txId} según Payku:`, paykuStatus);
            
            // Si Payku confirma que está cancelada o con error, actualizar en nuestra base de datos
            if (paykuStatus.status === 'cancelled' || paykuStatus.status === 'error') {
              console.log(`[CANCEL] Payku confirma que la transacción ${txId} está cancelada o con error`);
              
              // Registrar en localStorage para futuras referencias
              try {
                const cancelledTxsStr = localStorage.getItem('cancelled_transactions');
                let cancelledTxs = [];
                
                if (cancelledTxsStr) {
                  try {
                    cancelledTxs = JSON.parse(cancelledTxsStr);
                    if (!Array.isArray(cancelledTxs)) cancelledTxs = [];
                  } catch {}
                }
                
                if (!cancelledTxs.includes(txId)) {
                  cancelledTxs.push(txId);
                  localStorage.setItem('cancelled_transactions', JSON.stringify(cancelledTxs));
                }
              } catch (storageError) {
                console.error('[CANCEL] Error al guardar en localStorage:', storageError);
              }
            }
          } catch (paykuError) {
            console.error(`[CANCEL] Error al verificar estado con Payku para ${txId}:`, paykuError);
          }
          
          // Usar el verificador de transacciones para marcar como cancelada
          const cancelResult = await TransactionVerifier.markTransactionAsCancelled(
            txId,
            'user_cancel_page'
          );
          
          if (!cancelResult) {
            console.error(`[CANCEL] Error al marcar transacción ${txId} como cancelada`);
            allSuccessful = false;
          } else {
            console.log(`[CANCEL] Transacción ${txId} marcada como cancelada correctamente`);
            
            // Verificar si la transacción existía previamente
            const { data } = await supabase
              .from('transactions')
              .select('*')
              .eq('transaction_id', txId)
              .single();
              
            if (data) {
              toast.success('Pago cancelado correctamente');
            }
          }
        }
        
        // Actualizar el estado según el resultado
        setUpdateStatus(allSuccessful ? 'success' : 'error');
      } catch (error) {
        console.error('[CANCEL] Error general al procesar cancelación:', error);
        setUpdateStatus('error');
      } finally {
        // Iniciar cuenta regresiva para redirección
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              // Redirigir a la página de compra de monedas
              window.location.href = '/coins';
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        
        // Eliminar datos de la transacción del localStorage y sessionStorage
        try {
          localStorage.removeItem('pendingTransaction');
          sessionStorage.removeItem('pendingTransaction');
          localStorage.removeItem('payku_current_transaction');
          sessionStorage.removeItem('payku_transaction_to_verify');
          localStorage.removeItem('payku_transaction');
          sessionStorage.removeItem('payku_transaction');
          
          // Registrar que estuvimos en la página de cancelación
          localStorage.setItem('last_cancel_timestamp', Date.now().toString());
          localStorage.setItem('last_cancel_path', location.pathname + location.search);
        } catch (err) {
          console.error('[CANCEL] Error al limpiar almacenamiento:', err);
        }
      }
    };
    
    processCancellation();
  }, [location]);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-black flex items-center justify-center">
      <div className="bg-gradient-to-br from-gray-900 via-red-900/20 to-gray-900 p-8 rounded-xl shadow-lg max-w-md w-full text-center border border-red-500/30">
        <div className="mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/20 mb-4">
            <XCircle className="h-10 w-10 text-red-500" />
          </div>
        </div>
        
        <h1 className="text-2xl font-bold text-white mb-4">Pago Cancelado</h1>
        
        <p className="text-gray-300 mb-6">
          Has cancelado el proceso de pago. Si fue por error, puedes intentar nuevamente desde la página de compra de monedas.
        </p>
        
        {transactionId && (
          <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-3 mb-5">
            <p className="text-xs text-gray-400 mb-1">ID de transacción:</p>
            <p className="text-sm text-gray-300 font-mono">{transactionId}</p>
          </div>
        )}
        
        {updateStatus === 'error' && (
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 mb-5">
            <p className="text-sm text-red-400">
              Hubo un problema al registrar la cancelación. Sin embargo, no te preocupes, el pago no ha sido procesado.
            </p>
          </div>
        )}
        
        <div className="flex flex-col space-y-4">
          <Link 
            to="/coins" 
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-medium py-3 px-4 rounded-lg transition duration-300"
          >
            Volver a Comprar Monedas
          </Link>
          
          <Link 
            to="/" 
            className="bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium py-3 px-4 rounded-lg transition duration-300"
          >
            Volver al Inicio
          </Link>
          
          <p className="text-sm text-gray-400 mt-4">
            Serás redirigido automáticamente en {countdown} segundos...
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaykuCancel; 